import React from 'react'
import PropTypes from 'prop-types'
import Link, { withPrefix } from 'gatsby-link'
import pure from 'recompose/pure'
import kebabCase from 'lodash/kebabCase'
import Fade from '@material-ui/core/Fade'
import withStyles from '@material-ui/core/styles/withStyles'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import cx from 'classnames'
import Divider from 'components/Divider'
import Tags from 'components/Tags'
import ShareBar from 'components/ShareBar'
import SubscribeFormInArticle from 'components/forms/SubscribeFormInArticle'
import { ArticleMeta } from 'components/Meta'
import FloatingSidebar from 'components/FloatingSidebar'
import Quote from 'components/Articlepage/Quote'
import NetlifyForm from 'components/forms/NetlifyForm'
import { pageStyle } from 'config/theme'
import formatDate from 'utils/formatDate'

const styles = (theme) => ({
  content: {
    position: 'relative',
    ...pageStyle(theme),
    margin: 0,
    padding: 10,
    width: 700,
    [theme.breakpoints.down('sm')]: {
      width: '80%',
    },
  },
  thumbnailWrapper: {
    width: '100%',
    height: 430,
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      height: '100%',
    },
  },
  thumbnail: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.6s, opacity 1s ease-in !important',
    '&:hover': {
      transform: 'scale(1.3) rotate(-10deg)',
    },
  },
  title: {
    textTransform: 'none',
    fontWeight: 500,
    color: theme.palette.text.main,
    padding: 10,
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.4em',
      paddingTop: 0,
    },
  },
  originalAuthor: {
    color: theme.palette.text.soft,
    fontSize: '0.93em',
  },
  formatDate: {
    color: theme.palette.text.soft,
    paddingBottom: 10,
    textAlign: 'center',
    fontSize: '0.85em',
    fontWeight: 300,
    '&:hover': {
      opacity: 1,
    },
  },
  // format date when there's last updated
  withLastUpdated: {
    paddingBottom: 0,
  },
  paginateButton: {
    textTransform: 'none',
    background: theme.palette.primary.dark,
    transition: 'color 0.5s, background 0.5s',
    color: '#fff',
    margin: `0 ${theme.spacing.unit}px`,
    padding: 'auto 25px',
    borderRadius: theme.spacing.unit * 5,
    boxShadow: 'none',
    '&:hover': {
      color: theme.palette.primary.dark,
      background: theme.palette.borders.ghost,
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      fontSize: '0.8em',
    },
  },
  tagsContainer: {
    textAlign: 'center',
    minHeight: 35,
  },
  linkHover: {
    color: theme.palette.link.main,
    '&:hover': {
      color: theme.palette.link.hovering,
    },
  },
})

const PostTemplate = ({ classes, theme, data, pathContext, ...props }) => {
  const { previousPage, nextPage, articleBody } = pathContext
  const { authorNode, post } = data
  const baseUrl = 'https://healthfulinspired.com'

  const datePublished = new Date(post.frontmatter.date).toISOString()
  const dateModified = post.frontmatter.updateDate
    ? new Date(post.frontmatter.updateDate).toISOString()
    : post.frontmatter.updateDate

  let authorComponent = null,
    postIsUpdated = false

  if (post.frontmatter.displayAuthor && post.frontmatter.originalAuthor) {
    const { originalAuthor, editedBy } = post.frontmatter
    // Link to author page if it exists
    if (authorNode) {
      authorComponent = (
        <div>
          <Typography align="center" className={classes.originalAuthor}>
            Author:{' '}
            <Link to={authorNode.fields.slug} className={classes.linkHover}>
              {originalAuthor}
            </Link>
          </Typography>
          {editedBy && (
            <Typography align="center" className={classes.originalAuthor}>
              Edited By:{' '}
              <Link
                to={`/authors/${kebabCase(editedBy)}`}
                className={classes.linkHover}
              >
                {editedBy}
              </Link>
            </Typography>
          )}
        </div>
      )
    } else {
      authorComponent = (
        <Typography align="center" className={classes.originalAuthor}>
          Author: {originalAuthor}
        </Typography>
      )
    }
  }

  {
    const postDate = post.frontmatter.date
    const updateDate = post.frontmatter.updateDate // eslint-disable-line
    if (updateDate && updateDate > postDate) {
      postIsUpdated = true
    }
  }

  const Image = (props) => <img {...props} /> // Fade passes in mandatory style prop

  const shareLink = baseUrl + withPrefix(post.fields.slug)

  const hasQuote = post.frontmatter.quote
    ? !!post.frontmatter.quote.quote
    : false

  return (
    <div>
      <FloatingSidebar
        shareLink={shareLink}
        title={post.frontmatter.title}
        thumbnail={post.frontmatter.thumbnail}
      />
      <Grid
        className={classes.container}
        id="articlePage"
        spacing={24}
        justify="center"
        alignItems="center"
        style={{ padding: '20px 0' }}
        container
      >
        <ArticleMeta
          title={post.frontmatter.title}
          shortDescription={post.frontmatter.shortDescription}
          thumbnail={post.frontmatter.thumbnail}
          genre={post.frontmatter.category}
          tags={post.frontmatter.tags}
          wordCount={post.wordCount.words}
          datePublished={datePublished}
          dateModified={dateModified}
          articleBody={articleBody}
          excerpt={post.excerpt}
          path={post.fields.slug}
        />
        <Grid className={classes.content} item>
          <Typography
            variant="display1"
            align="center"
            classes={{ display1: classes.title }}
          >
            {post.frontmatter.title}
          </Typography>
          {authorComponent}
          <Typography
            align="center"
            className={cx(classes.formatDate, {
              [classes.withLastUpdated]: postIsUpdated === true,
            })}
          >
            {formatDate(post.frontmatter.date)} {'\u2022'} {post.timeToRead} min
            read
          </Typography>
          {postIsUpdated && (
            <Typography
              align="center"
              className={classes.formatDate}
              style={{ color: theme.palette.success }}
            >
              Last Updated: {formatDate(dateModified)}
            </Typography>
          )}
          <div className={classes.thumbnailWrapper}>
            <Fade timeout={2000} in>
              <Image
                className={classes.thumbnail}
                src={post.frontmatter.thumbnail}
                alt={post.frontmatter.title}
                title={post.frontmatter.title}
              />
            </Fade>
          </div>
          <div style={{ marginBottom: 10 }}>
            <SubscribeFormInArticle
              text={post.frontmatter.subscriptionTextBeginning}
              cta={post.frontmatter.subscriptionCTABeginning}
            />
          </div>
          {hasQuote && <Quote {...post.frontmatter.quote} />}
          <ShareBar
            label="Share this post"
            thumbnail={post.frontmatter.thumbnail}
            title={post.frontmatter.title}
            slug={post.fields.slug}
          />
          {/* <Typography align="center" variant="caption" style={{ opacity: 0.4 }}>
            We may receive a small commission from
          </Typography> */}
          <div
            className="markdown"
            dangerouslySetInnerHTML={{ __html: post.html }}
          />
          {post.frontmatter.displaySource &&
            post.frontmatter.source && (
              <Typography
                variant="subheading"
                style={{ padding: '15px 0', textTransform: 'none' }}
              >
                (This article was originally posted{' '}
                <a href={post.frontmatter.source} target="_blank">
                  here
                </a>)
              </Typography>
            )}
          <ShareBar
            label="Did this information help you? Help support us and share it"
            thumbnail={post.frontmatter.thumbnail}
            title={post.frontmatter.title}
            slug={post.fields.slug}
            showBottomCounts={false}
          />
          <Divider offset={15} />
          <Typography
            variant="title"
            style={{ fontWeight: 600, padding: 12 }}
            align="center"
          >
            Enjoyed the read?
          </Typography>
          <SubscribeFormInArticle
            text={post.frontmatter.subscriptionTextEnd}
            cta={post.frontmatter.subscriptionCTAEnd}
          />
          <Divider offset={25} />
          <EnhancedArticleNavigator
            previousPage={previousPage}
            nextPage={nextPage}
            className={classes.paginateButton}
          />
          <Divider offset={25} />
          <div className={classes.tagsContainer}>
            <Typography variant="body2">tags: &nbsp;</Typography>
            <Tags tags={post.frontmatter.tags} />
          </div>
        </Grid>
      </Grid>
      <style>{`
        .markdown img {
          width: 100%;
          max-height: 380px;
          object-fit: cover;
          border: 1px solid ${theme.palette.borders.cloudier};
        }
        .markdown {
          color: ${theme.palette.text.main};
        }
        table {
          border: 1px solid ${theme.palette.primary.main};
        }
        table th {
          background: ${theme.palette.primary.main};
          color: #fff;
          padding: 10px;
        }
        table td {
          border: 1px solid ${theme.palette.borders.cloud};
        }
        h1, h2, h3, h4, h5, h6 {
          line-height: 1em;
        }
        h1 {
          padding: 20px 0;
        }
        .instagram-media {
          margin: auto !important;
        }
        @media (max-width: 600px) {
          blockquote {
            margin: 0;
            padding-left: 8px;
            border-left: 10px solid ${theme.palette.borders.ghost};
          }
          .markdown h1, h2, h3 {
            font-size: 1.4em;
          }
          .markdown img {
            height: 100%;
          }
        }
        `}</style>
    </div>
  )
}

PostTemplate.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  pathContext: PropTypes.shape({
    previousPage: PropTypes.string,
    nextPage: PropTypes.string,
    articleBody: PropTypes.string.isRequired,
  }).isRequired,
}

function ArticleNavigator({ previousPage, nextPage, className }) {
  return (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Button
        component={previousPage && Link}
        to={previousPage}
        variant="flat"
        size="medium"
        className={className}
        style={{ opacity: !previousPage && 0.35 }}
        disabled={!previousPage}
      >
        Previous article
      </Button>
      <Button
        component={nextPage && Link}
        to={nextPage}
        variant="flat"
        size="medium"
        className={className}
        style={{ opacity: !nextPage && 0.35 }}
        disabled={!nextPage}
      >
        Next article
      </Button>
    </div>
  )
}

const EnhancedArticleNavigator = pure(ArticleNavigator)

// eslint-disable-next-line
export const query = graphql`
  query PostQuery($path: String!, $originalAuthor: String) {
    post: markdownRemark(fields: { slug: { eq: $path } }) {
      html
      excerpt(pruneLength: 300)
      timeToRead
      wordCount {
        words
      }
      fields {
        slug
      }
      frontmatter {
        title
        originalAuthor
        editedBy
        displayAuthor
        thumbnail
        source
        displaySource
        date
        updateDate
        shortDescription
        subscriptionTextBeginning
        subscriptionCTABeginning
        subscriptionTextEnd
        subscriptionCTAEnd
        tags
        category
        quote {
          author
          quote
          tags
        }
      }
    }
    authorNode: markdownRemark(
      frontmatter: {
        pageType: { eq: "author" }
        title: { eq: $originalAuthor }
      }
    ) {
      fields {
        slug
      }
    }
  }
`

export default withStyles(styles, { withTheme: true })(PostTemplate)



// WEBPACK FOOTER //
// ./src/templates/article.js